import { Box, FileHelpers, type TLAsset, type TLShape } from "tldraw";
import { ScreenshotPart } from "../../shared/types/PromptPart";
import { AgentRequest } from "../../shared/types/AgentRequest";
import { AgentHelpers } from "../AgentHelpers";
import { badgesTruncated, drawGutterRuler, drawInkBadges } from "../ink/badges";
import { convertTldrawIdToSimpleId } from "../../shared/format/convertTldrawShapeToBlurryShape";
import { lastInkChanges } from "../ink/inkChanges";
import {
  collectCacheableTypeset,
  typesetLayerBounds,
  typesetLayerCacheFor,
  typesetShapeExtent,
} from "../ink/typesetLayerCache";
import { canvasToDataUrl, rasterizeWithBlackInk } from "../ink/rasterize";
import { renderTypesetWithPageCss } from "../ink/typesetExportRenderer";
import { isAnnotationShape } from "../utils/annotationContent";
import { renderContentWithRetries } from "../utils/renderContentWithRetries";
import { PromptPartUtil, registerPromptPartUtil } from "./PromptPartUtil";

// Maximum pixel dimension for canvas screenshots sent to the LLM vision API.
const MAX_SCREENSHOT_PX = 3072;

/**
 * Board size at or under which the flat single-render path is used.
 *
 * Do not raise this. The flat path builds no 2D context, so it never runs
 * `annotate` — and `drawGutterRuler` with it. Those line numbers are the frame
 * the scene digest's "line 12" references resolve against, and the ruled paper
 * is a React overlay the exporter does not capture, so the composited ruler is
 * the model's only copy.
 */
const FLAT_PATH_MAX_SHAPES = 20;

const IMAGE_FAILURE_RETRY_MS = 30_000;

/**
 * Ceiling on waiting for webfonts before rendering. A face that never resolves
 * must not hold the turn; a screenshot in the wrong font is bad, no screenshot
 * is worse.
 */
const FONT_WAIT_TIMEOUT_MS = 2_000;

/**
 * Input:  no arguments
 * Output: resolves once no webfont is still loading, or after the ceiling
 *
 * KaTeX ships a face per symbol family and the browser fetches each one the
 * first time a glyph needs it, so a board that has just mounted is still
 * loading them. tldraw waits for its OWN fonts before it will render the
 * editor at all, and knows nothing about these.
 *
 * Rendering through that window is not a slow screenshot, it is a wrong one:
 * KaTeX puts the meaning in the face, so \mathbb{R} without KaTeX_AMS is the
 * plain letter R — not tofu, not a box, a different and entirely plausible
 * symbol, on a picture only the model ever reads.
 *
 * The ceiling is why this differs from `MathShapeUtil`'s measuring effect,
 * which awaits a bare `document.fonts.ready`. That effect holds nothing: a face
 * that never resolves costs one shape its re-measure, whenever it arrives. This
 * one holds the turn, so it must be able to give up.
 */
async function waitForWebfonts(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  if (document.fonts.status === "loaded") return;
  // Cleared rather than left to fire: the fonts usually win the race, and this
  // runs on every turn.
  let ceiling: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      document.fonts.ready.then(() => undefined),
      new Promise<void>((resolve) => {
        ceiling = setTimeout(resolve, FONT_WAIT_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (ceiling !== undefined) clearTimeout(ceiling);
  }
}
const yieldBeforeRetry = () =>
  new Promise<void>((resolve) => setTimeout(resolve, 0));

/**
 * An annotation drawn ON TOP of handwriting — a stroke across a digit deletes
 * the only copy of it the model gets. Only COLLIDING annotations go, so the
 * agent keeps sight of the rest of its own work. Boxes, not pixels.
 */
export function coversHandwriting(
  shape: TLShape,
  bounds: Box,
  inkBoxes: Box[],
): boolean {
  if (!isAnnotationShape(shape)) return false;
  return inkBoxes.some((ink) => Box.Collides(ink, bounds));
}

/**
 * Clusters that failed to render, as `label|strokeIds`. Keyed by content, so a
 * cluster the student then edits is retried rather than written off.
 */
const unrenderable = new WeakMap<object, Set<string>>();

export const failureKey = (cluster: { label: string; strokeIds: string[] }) =>
  `${cluster.label}|${[...cluster.strokeIds].sort().join(",")}`;

/**
 * The `changedInkLabels` field, or undefined to leave it off the wire.
 *
 * Three states, and `[]` is not a fallback: the gateway reads absent as
 * "unknown" and empty as "the student touched nothing". Something changed that
 * carries no badge — dropped past MAX_BADGES, for want of space, or as
 * unrenderable — is the former. Exported for tests.
 */
export function changedInkLabelsField(
  hasInk: boolean,
  badged: string[],
  anythingChanged: boolean,
): string[] | undefined {
  if (!hasInk) return undefined;
  if (badged.length > 0) return badged;
  return anythingChanged ? undefined : [];
}

export const ScreenshotPartUtil = registerPromptPartUtil(
  class ScreenshotPartUtil extends PromptPartUtil<ScreenshotPart> {
    static override type = "screenshot" as const;

    // A changed shape or immutable asset record retries immediately.
    private failedImages = new WeakMap<
      TLShape,
      { asset: TLAsset; retryAt: number }
    >();

    private rememberFailedImages(shapes: TLShape[]): void {
      if (shapes.length === 0) return;
      const images = shapes.filter((shape) => shape.type === "image");
      if (images.length === 0) return;
      console.error("[ScreenshotPartUtil] Images failed to render", {
        images: images.map((shape) => ({
          shapeId: shape.id,
          assetId: shape.props.assetId,
        })),
      });
      const retryAt = Date.now() + IMAGE_FAILURE_RETRY_MS;
      for (const shape of images) {
        if (!shape.props.assetId) continue;
        const asset = this.editor.getAsset(shape.props.assetId);
        if (asset) this.failedImages.set(shape, { asset, retryAt });
      }
    }

    override async getPart(
      request: AgentRequest,
      helpers: AgentHelpers,
    ): Promise<ScreenshotPart> {
      const { editor } = this;
      await waitForWebfonts();

      // Read at entry, before the markTurnCaptured calls below. Shapes drawn
      // while the render is awaited are not on this image.
      const hasViewportChanged = this.agent.requests.hasViewportChanged();
      const seenStudentWork = this.agent.requests.captureSeenStudentWork();

      const contextBoundsBox = Box.From(request.bounds);

      // Same grouping the digest uses, so the ids match. Computed before the
      // shape filter, which needs the extents to spot annotations over ink.
      const inkClusters = helpers.getStudentInkClusters(request.bounds);
      // Clusters known not to render are dropped up front — labelling writing
      // the image does not contain is the failure this guards against.
      const knownBad = unrenderable.get(this.agent) ?? new Set<string>();
      let liveClusters = inkClusters.filter(
        (c) => !knownBad.has(failureKey(c)),
      );
      const inkBoxes = liveClusters.map((cluster) => Box.From(cluster.bounds));
      // Their strokes go too, or they are re-rendered and re-bisected every
      // turn — the cost this cache exists to pay once.
      const badStrokes = new Set(
        inkClusters
          .filter((c) => knownBad.has(failureKey(c)))
          .flatMap((c) => c.strokeIds),
      );

      let hiddenAnnotations = 0;
      let visibleShapeCount = 0;
      const now = Date.now();
      const shapes = editor.getCurrentPageShapesSorted().filter((shape) => {
        if (shape.meta?.hideFromAgent) return false;
        if (badStrokes.has(convertTldrawIdToSimpleId(shape.id))) return false;
        const bounds = editor.getShapeMaskedPageBounds(shape);
        if (!bounds) return false;
        if (!contextBoundsBox.includes(bounds)) return false;
        if (coversHandwriting(shape, bounds, inkBoxes)) {
          hiddenAnnotations += 1;
          return false;
        }
        // Cached failures must not move a large board onto the ruler-free flat path.
        visibleShapeCount++;
        const failure = this.failedImages.get(shape);
        if (
          failure &&
          failure.retryAt > now &&
          shape.type === "image" &&
          shape.props.assetId
        ) {
          if (editor.getAsset(shape.props.assetId) === failure.asset)
            return false;
        }
        return true;
      });

      if (shapes.length === 0) {
        this.agent.requests.markTurnCaptured(seenStudentWork);
        return { type: "screenshot", screenshot: "", hasViewportChanged };
      }

      const largestDimension = Math.max(request.bounds.w, request.bounds.h);
      const scale =
        largestDimension > MAX_SCREENSHOT_PX
          ? MAX_SCREENSHOT_PX / largestDimension
          : 1;

      const outputW = Math.round(request.bounds.w * scale);
      const outputH = Math.round(request.bounds.h * scale);
      const badgeGeometry = {
        contextBounds: request.bounds,
        scale,
        outputW,
        outputH,
      };
      /** Clusters that got a label ON the image; only `annotate` raises it. */
      let labelledClusters = 0;
      /** Ids that actually got a badge — what the caption may point at. */
      let placedLabels: string[] = [];
      /** Badges drop any label they cannot place off the ink; report the shortfall. */
      const annotate = (ctx: CanvasRenderingContext2D) => {
        drawGutterRuler(ctx, badgeGeometry);
        placedLabels = drawInkBadges(ctx, liveClusters, badgeGeometry);
        const labelled = placedLabels.length;
        labelledClusters = labelled;
        if (labelled < liveClusters.length) {
          const reason = badgesTruncated(inkClusters.length)
            ? "past the 40-label cap"
            : "no clear space beside the writing";
          console.warn(
            `[ScreenshotPartUtil] labelled ${labelled} of ${liveClusters.length} ink clusters (${reason}). The scene digest still names and locates all of them.`,
          );
        }
      };

      let screenshotDataUrl: string;

      if (
        inkClusters.length === 0 &&
        visibleShapeCount <= FLAT_PATH_MAX_SHAPES
      ) {
        // Nothing to label or recolour, so no 2D context is needed.
        const imageOpts = {
          format: "jpeg" as const,
          background: true,
          // Light, like the layered path: one kind of sheet, every turn.
          darkMode: false,
          bounds: contextBoundsBox,
          padding: 0,
          pixelRatio: 1,
          scale,
        };
        // One render for everything, math and rich-text included: they have no
        // `toSvg`, so tldraw draws them with the foreignObject that is the only
        // thing that renders KaTeX. Slow, and this path is capped at
        // FLAT_PATH_MAX_SHAPES precisely because it does no layering — there is
        // no typeset pass here to cache.
        const recovered = await renderContentWithRetries(
          shapes,
          (batch) => editor.toImage(batch, imageOpts),
          yieldBeforeRetry,
        );
        if (!recovered.value) {
          this.agent.requests.markTurnCaptured(seenStudentWork);
          return { type: "screenshot", screenshot: "", hasViewportChanged };
        }
        this.rememberFailedImages(recovered.failed);
        screenshotDataUrl = await FileHelpers.blobToDataUrl(
          recovered.value.blob,
        );
      } else {
        // Layered so the handwriting can be flattened to black.
        const clusterOf = new Map<string, string>();
        for (const cluster of liveClusters) {
          for (const id of cluster.strokeIds) clusterOf.set(id, cluster.label);
        }
        // The worksheet's own typeset shapes, page-wide and annotation-free,
        // so the bitmap survives a camera move and the next stroke alike.
        //
        // Page-wide is a deliberate break from the `shapes` filter above, which
        // keeps only what the frame CONTAINS. The blit is clipped to the frame,
        // so the difference shows on one shape: a formula straddling the edge
        // used to be dropped whole and is now painted cropped — which is what
        // the student is looking at, and better than a missing equation.
        const cacheableTypeset = collectCacheableTypeset(editor);
        const typesetLayer = typesetLayerCacheFor(
          this.agent,
          cacheableTypeset,
          typesetLayerBounds(editor, cacheableTypeset),
          scale,
          typesetShapeExtent(editor),
        );
        const raster = await rasterizeWithBlackInk(shapes, {
          bounds: contextBoundsBox,
          scale,
          outputW,
          outputH,
          typesetLayer: typesetLayer ?? undefined,
          // Draws what it can from page CSS (→ typesetExportRenderer).
          renderTypeset: (typesetShapes, bounds, ctx) =>
            renderTypesetWithPageCss(editor, typesetShapes, bounds, ctx),
          // No `batchSize`: one render for the content layer, one for the ink.
          inkGroupOf: (shape) =>
            clusterOf.get(convertTldrawIdToSimpleId(shape.id)) ?? "",
          renderBatch: (batch, imageOpts) => editor.toImage(batch, imageOpts),
          // Yield the main thread between renders so audio onmessage callbacks
          // can fire.
          onBatchBoundary: yieldBeforeRetry,
        });
        if (!raster) {
          this.agent.requests.markTurnCaptured(seenStudentWork);
          return { type: "screenshot", screenshot: "", hasViewportChanged };
        }

        this.rememberFailedImages(raster.droppedContentShapes);

        if (raster.droppedInkKeys.length > 0) {
          const dropped = new Set(raster.droppedInkKeys);
          const bad = unrenderable.get(this.agent) ?? new Set<string>();
          for (const cluster of liveClusters) {
            if (dropped.has(cluster.label)) bad.add(failureKey(cluster));
          }
          unrenderable.set(this.agent, bad);
          liveClusters = liveClusters.filter((c) => !dropped.has(c.label));
        }

        // Annotations last, so no layer renders over them.
        annotate(raster.ctx);

        screenshotDataUrl = await canvasToDataUrl(raster.ctx.canvas);
      }

      this.agent.requests.markTurnCaptured(seenStudentWork);

      // Only ids with a badge on the image: the caption tells the model to look
      // for an amber box, so naming one it cannot find is worse than silence.
      const placed = new Set(placedLabels);
      const changedInkLabels = liveClusters
        .filter((cluster) => cluster.changed && placed.has(cluster.label))
        .map((cluster) => cluster.label);
      // A vanished cluster changed the board but can never carry a badge, so it
      // counts here and not above.
      const changedField = changedInkLabelsField(
        inkClusters.length > 0,
        changedInkLabels,
        lastInkChanges(this.agent).size > 0,
      );

      return {
        type: "screenshot",
        screenshot: screenshotDataUrl,
        hasViewportChanged,
        // Omitted when nothing was removed, so the caption stays silent.
        ...(hiddenAnnotations > 0
          ? { hiddenAnnotationCount: hiddenAnnotations }
          : {}),
        ...(changedField !== undefined
          ? { changedInkLabels: changedField }
          : {}),
        // Only the labelled clusters count: these are exactly the ones the
        // model can correlate by id, and on the paths that never annotate it
        // is none of them. NOTE this field is informational on the wire — the
        // gateway overwrites it in `stampInkClusterCount` from its own
        // `inkClusters` part, which still counts clusters rather than labels,
        // so the caption can still name ids the image dropped. Fixing that
        // gate is a gateway-side change; this is the honest client number.
        inkClusterCount: labelledClusters,
      };
    }
  },
);
